import { useState, useEffect, useCallback } from 'react';
import { graphql } from '../../post';

/**
 * Hook personnalisé pour la logique métier du composant Layout
 *
 * @param {Object} params - Paramètres du hook
 * @param {Object} params.context - Contexte de l'application
 * @returns {Object} État et handlers du composant
 */
export const useLayoutLogic = ({ context }) => {
  const [state, setState] = useState({
    loaded: false,
    me: undefined,
    variables: {},
  });

  // Chargement des données utilisateur
  const loadUserData = useCallback(async () => {
    try {
      const res = await graphql(context.fetch, '{me}');
      setState((prev) => ({
        ...prev,
        me: res.me,
        loaded: true,
      }));
    } catch (error) {
      console.error('Error loading user data:', error);
      setState((prev) => ({
        ...prev,
        loaded: true,
      }));
    }
  }, [context.fetch]);

  // Gestion des événements
  const emitEvent = useCallback((type, data, cb = () => {}) => {
    switch (type) {
      case 'setVariable':
        setState((prev) => {
          const newVariables = { ...prev.variables };
          // Remplacement de lodash par Object.entries
          Object.entries(data).forEach(([key, value]) => {
            newVariables[key] = value;
          });
          return {
            ...prev,
            variables: newVariables,
          };
        });
        cb();
        break;
      default:
        // Émettre un événement personnalisé vers le composant Popup
        const customEvent = new CustomEvent('popupEvent', {
          detail: { type, data, cb },
        });
        window.dispatchEvent(customEvent);
        break;
    }
  }, []);

  // Effet de chargement des données
  useEffect(() => {
    loadUserData();
  }, [loadUserData]);

  return {
    state,
    handlers: {
      emitEvent,
    },
  };
};

/**
 * Fonction utilitaire pour valider le contexte
 *
 * @param {Object} context - Contexte à valider
 * @returns {Object} Résultat de la validation
 */
export const validateContext = (context) => {
  if (!context || typeof context !== 'object') {
    return {
      isValid: false,
      error: 'Context must be an object',
    };
  }

  if (typeof context.fetch !== 'function') {
    return {
      isValid: false,
      error: 'Context must have a fetch function',
    };
  }

  return {
    isValid: true,
    error: null,
  };
};

/**
 * Fonction utilitaire pour formater les variables
 *
 * @param {Object} variables - Variables à formater
 * @returns {Object} Variables formatées
 */
export const formatVariables = (variables) => {
  if (!variables || typeof variables !== 'object') {
    return {};
  }

  return Object.entries(variables).reduce((acc, [key, value]) => {
    acc[key] = value;
    return acc;
  }, {});
};
